import { CircleDot, Library, Play, Trash2, Upload, Video } from "lucide-react";
import { useRef, useState, type FC } from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { Progress } from "@/components/ui/progress";
import { WelcomeVideoPlayer } from "@/features/foreignNationalOutgoingIntakes/components/WelcomeVideoPlayer";

import type { VideoUpload } from "./uploadStore";

type Props = {
  videoUrl: string | null;
  upload: VideoUpload | null;
  /** Who the video greets, for the empty state ("Say hello to Priya"). */
  who: string;
  readOnly?: boolean;
  onRecord: () => void;
  onUpload: (file: File) => void;
  onRemove: () => void;
  /** "Use a Previous Video" (the video library), when there is one. */
  onLibrary?: () => void;
  libraryLabel?: string;
};

/**
 * The welcome video in three states: none (Record, Upload, Use a Previous
 * Video), uploading (progress, Cancel) and ready (Watch, Replace, Remove).
 */
export const WelcomeVideoCard: FC<Props> = ({
  videoUrl,
  upload,
  who,
  readOnly,
  onRecord,
  onUpload,
  onRemove,
  onLibrary,
  libraryLabel = "Use a Previous Video"
}) => {
  const fileRef = useRef<HTMLInputElement>(null);
  const [watching, setWatching] = useState(false);

  const fileInput = (
    <input
      ref={fileRef}
      type="file"
      accept="video/*"
      className="hidden"
      aria-label="Upload a welcome video"
      onChange={(e) => {
        const f = e.target.files?.[0];
        if (f) onUpload(f);
        e.currentTarget.value = "";
      }}
    />
  );

  if (upload) {
    return (
      <div
        className="flex flex-col gap-2 rounded-lg bg-fill-1 p-3"
        data-testid="welcome-video-uploading"
      >
        <div className="flex items-center gap-2 text-base text-fg">
          <Video className="size-4 text-fg-3" />
          <span className="min-w-0 flex-1">Uploading the welcome video…</span>
          <span className="text-sm text-fg-3 tabular-nums">
            {Math.round(upload.progress * 100)}%
          </span>
        </div>
        <Progress
          value={Math.round(upload.progress * 100)}
          aria-label="Upload progress"
          className="bg-fill-3"
        />
        <div className="flex items-center justify-between gap-2">
          <span className="text-sm text-fg-3">
            Keep working. Send waits until it&apos;s done.
          </span>
          <Button variant="ghost" size="xs" onClick={upload.cancel}>
            Cancel
          </Button>
        </div>
      </div>
    );
  }

  if (videoUrl) {
    return (
      <div className="flex items-center gap-3 rounded-lg bg-fill-1 p-2">
        <button
          type="button"
          className="relative flex aspect-video w-24 shrink-0 items-center justify-center overflow-hidden rounded-md bg-fg text-surface"
          onClick={() => setWatching(true)}
          aria-label="Watch the welcome video"
        >
          <video
            src={videoUrl}
            muted
            preload="metadata"
            className="absolute inset-0 size-full object-cover opacity-70"
          >
            <track kind="captions" />
          </video>
          <Play className="relative size-4" />
        </button>
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <span className="text-base text-fg">Welcome Video</span>
          <div className="flex flex-wrap items-center gap-1">
            <Button variant="ghost" size="xs" onClick={() => setWatching(true)}>
              Watch
            </Button>
            {readOnly ? null : (
              <>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="xs">
                      Replace
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-52">
                    <DropdownMenuItem onSelect={onRecord}>
                      <CircleDot /> Record
                    </DropdownMenuItem>
                    <DropdownMenuItem onSelect={() => fileRef.current?.click()}>
                      <Upload /> Upload
                    </DropdownMenuItem>
                    {onLibrary ? (
                      <DropdownMenuItem onSelect={onLibrary}>
                        <Library /> {libraryLabel}
                      </DropdownMenuItem>
                    ) : null}
                  </DropdownMenuContent>
                </DropdownMenu>
                <Button
                  variant="ghost"
                  size="xs"
                  className="text-danger"
                  onClick={onRemove}
                >
                  <Trash2 /> Remove
                </Button>
              </>
            )}
          </div>
        </div>
        {fileInput}
        <Dialog open={watching} onOpenChange={setWatching}>
          <DialogContent className="sm:max-w-2xl">
            <DialogHeader>
              <DialogTitle>Welcome Video</DialogTitle>
            </DialogHeader>
            <div className="aspect-video w-full overflow-hidden rounded-lg bg-fg">
              {watching ? (
                <WelcomeVideoPlayer src={videoUrl} objectFit="contain" />
              ) : null}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3 rounded-lg p-3 ring-1 ring-line-2 ring-inset">
      <div className="flex items-start gap-2">
        <Video className="mt-0.5 size-4 shrink-0 text-fg-3" />
        <div className="flex min-w-0 flex-col">
          <span className="text-base text-fg">Say hello to {who}</span>
          <span className="text-sm text-fg-3">
            A short video from you on the first screen. Talking points are
            drafted for you.
          </span>
        </div>
      </div>
      {readOnly ? null : (
        <div className="flex flex-wrap items-center gap-1.5">
          <Button variant="secondary" size="xs" onClick={onRecord}>
            <CircleDot /> Record
          </Button>
          <Button
            variant="ghost"
            size="xs"
            onClick={() => fileRef.current?.click()}
          >
            <Upload /> Upload
          </Button>
          {onLibrary ? (
            <Button variant="ghost" size="xs" onClick={onLibrary}>
              <Library /> {libraryLabel}
            </Button>
          ) : null}
        </div>
      )}
      {fileInput}
    </div>
  );
};
